import { createElement, useCallback, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { nearestIndex } from './chartUtils.ts';

/**
 * Crosshair state for a chart: hover with a mouse, press-and-drag to scrub on touch (vertical
 * page scrolling still works because the overlay only claims horizontal pans), arrow keys when
 * the chart has focus.
 */
export function useScrub(xs: readonly number[]) {
  const [index, setIndex] = useState<number | null>(null);

  const locate = useCallback(
    (e: PointerEvent<Element>) => {
      if (xs.length === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setIndex(nearestIndex(xs, e.clientX - rect.left));
    },
    [xs],
  );

  const handlers = {
    onPointerMove: (e: PointerEvent<Element>) => {
      if (e.pointerType === 'mouse' || e.buttons > 0 || e.pointerType === 'touch') locate(e);
    },
    onPointerDown: (e: PointerEvent<Element>) => locate(e),
    onPointerLeave: (e: PointerEvent<Element>) => {
      if (e.pointerType === 'mouse') setIndex(null);
    },
    onPointerUp: (e: PointerEvent<Element>) => {
      if (e.pointerType !== 'mouse') window.setTimeout(() => setIndex(null), 1600);
    },
    onPointerCancel: () => setIndex(null),
    onKeyDown: (e: KeyboardEvent<Element>) => {
      if (xs.length === 0) return;
      const step = e.shiftKey ? 12 : 1;
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(xs.length - 1, (i ?? -1) + step));
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, (i ?? xs.length) - step));
      else if (e.key === 'Home') setIndex(0);
      else if (e.key === 'End') setIndex(xs.length - 1);
      else if (e.key === 'Escape') setIndex(null);
      else return;
      e.preventDefault();
    },
    onBlur: () => setIndex(null),
  };

  return { index, setIndex, handlers };
}

/**
 * The chart's keyboard/pointer surface and its text alternative: an HTML slider laid over the
 * plot (the SVG itself is decorative), so no interactive element sits inside an image.
 */
export function ScrubOverlay({ left, top, width, height, label, valuetext, max, index, handlers }: {
  left: number;
  top: number;
  width: number;
  height: number;
  label: string;
  valuetext: string | undefined;
  max: number;
  index: number | null;
  handlers: ReturnType<typeof useScrub>['handlers'];
}) {
  return createElement('div', {
    className: 'chart__overlay',
    role: 'slider',
    tabIndex: 0,
    'aria-label': label,
    'aria-valuemin': 0,
    'aria-valuemax': max,
    'aria-valuenow': index ?? 0,
    'aria-valuetext': valuetext ?? label,
    style: { left, top, width, height },
    ...handlers,
  });
}
